import Dispatcher from '../dispatcher/CentricDispatcher.react';
import { ActionTypes } from '../constants/CentricConstants';
import { ReduceStore } from 'flux/utils';

const merge = (source, elements) => {
    if (!elements) { return source; } // guard
    if (Array.isArray(elements)) {
        const newElements = elements && elements.filter(e => !source.includes(e));
        if (!newElements || newElements.length === 0) { return source; }
        return source.concat(newElements);
    } else {
        return source.includes(elements) ? source : source.concat(elements);
    }
};

// const replace = (source)
const createDraft = () => ({
    id: null,
    name: '',
    skills: []
});

const addElement = (state, {entities, result}, {keepDraft = true, isFetching = false} = {}) => ({
    draft: (keepDraft ? state.draft : null),
    compliance: {
        ...state.compliance,
        ...entities.compliance,
    },
    skills: {
        ...state.skills,
        ...entities.skill,
    },
    list: merge(state.list, result)
});

const getElementWithSkills = (element, skills) => ({
    ...element,
    skills: element.skills.map(i => skills[i])
});
const getSimpleElement = ({id, name}) => ({id, name});

class ComplianceStore extends ReduceStore {

    getInitialState() {
        return {
            isFetching: false,
            draft: null,
            compliance: {},
            skills: {},
            list: [],
        };
    }

    getList() {
        const { list, compliance, skills } = this.getState();
        return !list ? [] : list.map(i => getElementWithSkills(compliance[i], skills));
    }

    get(id) {
        return this.getState().compliance[id];
    }

    getDraft() {
        const { draft, skills } = this.getState();
        return draft && {
            ...draft,
            skills: draft.skills.map(i => skills[i])
        };
    }

    searchByName(searchText, fullDetails = false) {
        if (!searchText) { return []; }
        const { list, compliance, skills } = this.getState();
        const pattern = new RegExp(searchText, "i");
        return list.filter(i => pattern.test(compliance[i].name))
            .map(fullDetails
                ? i => getElementWithSkills(compliance[i], skills)
                : i => getSimpleElement(compliance[i])
            );
    }

    reduce(state, action) {
        let draft;
        switch (action.type) {
            case ActionTypes.COMPLIANCE_LIST_REQUEST:
                return {...state, isFetching: true};

            case ActionTypes.COMPLIANCE_LIST_SUCCESS:
                return {
                    ...state,
                    compliance: {
                        ...state.compliance,
                        ...action.data.entities.compliance,
                    },
                    skills: {
                        ...state.skills,
                        ...action.data.entities.skill,
                    },
                    list: merge(state.list, action.data.result)
                };

            case ActionTypes.COMPLIANCE_LIST_FAILURE:
                return {...state, isFetching: false};

            case ActionTypes.COMPLIANCE_INFO_REQUEST:
                return {...state, isFetching: true};

            case ActionTypes.COMPLIANCE_INFO_SUCCESS:
                return addElement(state, action.data);

            case ActionTypes.COMPLIANCE_INFO_FAILURE:
                return {...state, isFetching: false};

            case ActionTypes.COMPLIANCE_ADD_REQUEST:
                return {...state, isFetching: true};

            case ActionTypes.COMPLIANCE_ADD_SUCCESS:
                return addElement(state, action.data);

            case ActionTypes.COMPLIANCE_ADD_FAILURE:
                return {...state, isFetching: false};

            case ActionTypes.COMPLIANCE_UPDATE_REQUEST:
                return {
                    ...state,
                    isFetching: true,
                    compliance: {
                        ...state.compliance,
                        [action.payload.draft.id]: {...action.payload.draft}
                    }
                };

            case ActionTypes.COMPLIANCE_UPDATE_SUCCESS:
                return addElement(state, action.data);

            case ActionTypes.COMPLIANCE_UPDATE_FAILURE:
                return {
                    ...state,
                    isFetching: false,
                    compliance: {
                        ...state.compliance,
                        [action.payload.current.id]: {...action.payload.current}
                    }
                };

            case ActionTypes.COMPLIANCE_DELETE_REQUEST:
                return {...state, isFetching: true};

            case ActionTypes.COMPLIANCE_DELETE_SUCCESS:
                let compliance = {...state.compliance};
                delete compliance[action.payload.id];
                return {
                    ...state,
                    isFetching: false,
                    compliance,
                    list: Object.keys(compliance)
                };

            case ActionTypes.COMPLIANCE_DELETE_FAILURE:
                return {...state, isFetching: false};

            case ActionTypes.COMPLIANCE_ASSIGN_USERS_REQUEST:
                return {...state, isFetching: true};

            case ActionTypes.COMPLIANCE_ASSIGN_USERS_SUCCESS:
                return addElement(state, action.data);

            case ActionTypes.COMPLIANCE_ASSIGN_USERS_FAILURE:
                return {...state, isFetching: false};

            case ActionTypes.COMPLIANCE_CLEAR_USERS_REQUEST:
                return {...state, isFetching: true};

            case ActionTypes.COMPLIANCE_CLEAR_USERS_SUCCESS:
                return addElement(state, action.data);

            case ActionTypes.COMPLIANCE_CLEAR_USERS_FAILURE:
                return {...state, isFetching: false};

        // editing the draft:
            case ActionTypes.COMPLIANCE_NEW:
                return {
                    ...state,
                    draft: createDraft()
                };

            case ActionTypes.COMPLIANCE_EDIT:
                return {
                    ...state,
                    draft: {...action.payload.draft},
                };

            case ActionTypes.COMPLIANCE_DISCARD:
                return {
                    ...state,
                    draft: null
                };

            case ActionTypes.COMPLIANCE_UPDATE_DRAFT:
                return {
                    ...state,
                    draft: {
                        ...state.draft,
                        [action.payload.field]: action.payload.value
                    }
                };

            case ActionTypes.COMPLIANCE_ADD_SKILL:
                const { skill } = action.payload;
                return {
                    ...state,
                    skills: {
                        ...state.skills,
                        [skill.id]: skill
                    },
                    draft: state.draft && {
                        ...state.draft,
                        skills: state.draft.skills.includes(skill.id) ? state.draft.skills : state.draft.skills.concat(skill.id)
                    }
                };

            case ActionTypes.COMPLIANCE_REMOVE_SKILL:
                return {
                    ...state,
                    draft: state.draft && {
                        ...state.draft,
                        skills: state.draft.skills.filter(id => (id !== action.payload.skillId))
                    }
                };

            default:
                return state;
        }
    }
}

export default new ComplianceStore(Dispatcher);
